import {
  Box,
  Button,
  Dialog,
  DialogActions,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { SearchFillIcon } from 'src/assets/icons/SearchFillIcon';
import { CustomWaterLoading } from 'src/components/streametric/custom-water-loading/CustomWaterLoading';
import { useAuthentication } from 'src/features/authentication/context';
import { StyledSelectedDataBox } from 'src/features/sites/components/site-trends-chart-modal/style';
import {
  DialogContentWithCustomScroll,
  formProviderWrapper,
  modalContentItemsList,
  selectDataTitle,
  StyledModalWrapper,
} from 'src/features/trends/components/add-chart-modal-content/style';
import { tagListItem } from '../../config/pdfReportConfig';
import { ModalContentItem } from './ModalContentItem';
import { TagResponse } from 'src/types/tags/tags';
import { GatewayInputDataTypeEnum } from 'src/types/gateways';

type props = {
  state: boolean;
  onToggle: () => void;
  siteId: string;
  tagList: tagListItem[];
  onChange: (tagItem: tagListItem[]) => void;
  tags: TagResponse[];
  isLoading: boolean;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
};

export const AddTagDialog = ({
  state,
  onToggle,
  siteId,
  tagList,
  onChange,
  tags,
  isLoading,
  setSearch,
}: props) => {
  const theme = useTheme();
  const disabledTextColor = theme.palette.text.disabled;

  useEffect(() => {
    if (!state) {
      setSearch('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const [selectedIds, setSelectedIds] = useState<string[]>(tagList.map((t) => t.inputId));
  // `tags` only holds the current search's results, so tags ticked under an earlier search are kept here.
  const [pickedTags, setPickedTags] = useState<Record<string, TagResponse>>({});

  useEffect(() => {
    if (state) {
      setSelectedIds(tagList.map((t) => t.inputId));
      setPickedTags({});
    }
  }, [state, tagList]);
  const {
    customerId: { value: customerId },
  } = useAuthentication();

  const visibleTags =
    tags?.filter((tag) => !tag.hidden && tag.dataType !== GatewayInputDataTypeEnum.CALCULATED) ||
    [];

  const toggleTag = (tag: TagResponse) => {
    setPickedTags((prev) => ({ ...prev, [tag.id]: tag }));
    setSelectedIds((prev) =>
      prev.includes(tag.id) ? prev.filter((id) => id !== tag.id) : [...prev, tag.id]
    );
  };

  const onAddTagHandler = () => {
    if (!customerId || !siteId) return;

    const allTagsMap = new Map(
      [...Object.values(pickedTags), ...(tags || [])].map((tag) => [tag.id, tag])
    );

    const updated: tagListItem[] = selectedIds.flatMap((id, index) => {
      const tag = allTagsMap.get(id);
      if (!tag) {
        const existing = tagList.find((t) => t.inputId === id);
        return existing ? [existing] : [];
      }
      return [
        {
          gatewayId: tag.gatewayId,
          inputId: tag.id,
          rawInputName: tag.rawInputName ?? `tag - ${index}`,
          unit: tag.rawUnit,
          order: index,
        },
      ];
    });

    onChange(updated);
    onToggle();
  };

  const onCloseHandler = () => {
    setSearch('');
    setSelectedIds(tagList.map((t) => t.inputId));
    onToggle();
  };

  return (
    <Dialog open={state} onClose={onCloseHandler} maxWidth="lg">
      <DialogContentWithCustomScroll sx={formProviderWrapper}>
        <StyledModalWrapper theme={theme}>
          <StyledSelectedDataBox>
            <Typography variant="body1" sx={selectDataTitle}>
              Select Data
            </Typography>
            <TextField
              onChange={(event) => {
                setSearch(event.target.value);
              }}
              fullWidth
              placeholder="Search tags..."
              inputProps={{ style: { padding: '10px' }, 'data-sm': 'search-input' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchFillIcon fill={disabledTextColor} />
                  </InputAdornment>
                ),
              }}
            />
          </StyledSelectedDataBox>

          {isLoading ? (
            <Stack justifyContent="center" alignItems="center" sx={{ mb: 2 }} flexDirection={'row'}>
              <CustomWaterLoading />
            </Stack>
          ) : (
            <Box sx={modalContentItemsList}>
              {visibleTags.map((tag) => (
                <ModalContentItem
                  key={tag.id}
                  tag={tag}
                  checked={selectedIds.includes(tag.id)}
                  onToggle={() => toggleTag(tag)}
                />
              ))}
            </Box>
          )}
        </StyledModalWrapper>
      </DialogContentWithCustomScroll>
      <Divider />
      <DialogActions sx={{ backgroundColor: theme.palette.background.default }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button onClick={onCloseHandler} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={onAddTagHandler}
            variant="contained"
            disabled={(visibleTags || []).length === 0}
          >
            Add
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
